import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { SITE } from "@/content/site";

type Term = {
  term: string;
  slug: string;
  definition: string;
  /** Optional internal link to a deeper guide */
  readMore?: string;
  readMoreLabel?: string;
};

const TERMS: Term[] = [
  { term: "AEO", slug: "aeo", definition: "Answer Engine Optimization — the practice of structuring content, schema and entity signals so that LLM-powered answer engines (ChatGPT, Perplexity, Claude, Gemini, Google AI Overviews) cite your brand as a source inside generated answers.", readMore: "/blog/what-is-aeo-how-to-get-cited-by-chatgpt-2026", readMoreLabel: "What is AEO?" },
  { term: "AI Overviews", slug: "ai-overviews", definition: "Google's generative answer block that renders at the top of a SERP, synthesised from multiple sources with inline citations. Appears on roughly half of US informational queries as of 2026.", readMore: "/blog/ai-overviews-vs-featured-snippets-2026", readMoreLabel: "AI Overviews vs Featured Snippets" },
  { term: "Article schema", slug: "article-schema", definition: "Schema.org Article type that describes a written piece with headline, author, datePublished, dateModified and publisher. Required for Google Discover eligibility and a strong AEO signal.", readMore: "/blog/schema-markup-for-aeo-2026", readMoreLabel: "Schema for AEO" },
  { term: "Backlink", slug: "backlink", definition: "An inbound hyperlink from another website to yours. Backlinks remain a primary Google ranking factor and a meaningful AEO signal because LLMs over-index on pages linked from sources they already trust (Wikipedia, Reddit, news domains)." },
  { term: "Bangla SEO", slug: "bangla-seo", definition: "SEO targeted at Bangla-language queries — roughly 60% of search demand in Bangladesh. Requires Bangla titles/H1s, hreflang bn-BD, and Bangla-language entity associations.", readMore: "/blog/bangla-language-seo-bd-search-demand", readMoreLabel: "Bangla-Language SEO" },
  { term: "BreadcrumbList", slug: "breadcrumblist", definition: "Schema.org type that describes a page's position in the site hierarchy. Enables breadcrumb rich results in Google and helps LLMs map your URL structure to topical clusters." },
  { term: "Canonical URL", slug: "canonical", definition: "The `<link rel=\"canonical\">` tag declaring the preferred URL for a piece of content. Misconfigured canonicals are a top cause of pages being de-indexed or having their content attributed to the wrong URL." },
  { term: "Citation (AEO)", slug: "citation", definition: "A named reference to a brand inside an LLM-generated answer, with or without a clickable link. The core unit of AEO measurement — share of citations replaces share of clicks." },
  { term: "CLS", slug: "cls", definition: "Cumulative Layout Shift — a Core Web Vital measuring unexpected visual movement during page load. Target <0.1. Common causes: images without dimensions, ads injected above the fold, late-loading web fonts.", readMore: "/blog/core-web-vitals-2026-inp-lcp-cls", readMoreLabel: "Core Web Vitals 2026" },
  { term: "Crawl budget", slug: "crawl-budget", definition: "The number of URLs a search engine bot will fetch from your site in a given period. Becomes a constraint past ~10k indexable URLs; managed via robots.txt, internal linking and removing low-value pages.", readMore: "/blog/crawl-budget-optimization-small-sites", readMoreLabel: "Crawl Budget for Small Sites" },
  { term: "Core Web Vitals", slug: "core-web-vitals", definition: "Google's three user-experience metrics: LCP (loading), INP (interactivity, replaced FID in 2024) and CLS (visual stability). A confirmed ranking signal for both desktop and mobile.", readMore: "/blog/core-web-vitals-2026-inp-lcp-cls" },
  { term: "E-E-A-T", slug: "eeat", definition: "Google's quality framework — Experience, Expertise, Authoritativeness, Trust. Not a direct ranking factor but the lens raters use to evaluate pages, especially YMYL (health, finance, legal). Strong author entities, original research and citations all raise E-E-A-T." },
  { term: "Entity", slug: "entity", definition: "A uniquely identifiable thing (a person, brand, place, concept) in Google's Knowledge Graph or an LLM's training data. A brand that search engines and AI assistants recognise as a clear entity is easier for them to describe and cite accurately.", readMore: "/blog/entity-seo-signals-for-aeo", readMoreLabel: "Entity SEO Signals" },
  { term: "FAQPage schema", slug: "faqpage", definition: "Schema.org type wrapping Question/Answer pairs. The highest-leverage on-page AEO lever — directly machine-readable by ChatGPT, Perplexity and AI Overviews." },
  { term: "GEO", slug: "geo", definition: "Generative Engine Optimization — the broader discipline of being visible across all generative AI surfaces including answer engines, AI shopping assistants and image generators. AEO is a subset of GEO focused on answer engines." },
  { term: "Google Business Profile", slug: "gbp", definition: "Google's free local business listing (formerly Google My Business) that powers the map pack and local knowledge panel. The single highest-ROI asset for any local SEO program.", readMore: "/blog/google-business-profile-optimization-checklist-2026", readMoreLabel: "GBP Optimization Checklist" },
  { term: "hreflang", slug: "hreflang", definition: "HTML/HTTP/sitemap annotation telling search engines which language and regional version of a page to serve. Critical for multi-language sites — a missing or self-referential x-default is a common bug.", readMore: "/blog/hreflang-done-right-multi-country-seo", readMoreLabel: "hreflang Done Right" },
  { term: "INP", slug: "inp", definition: "Interaction to Next Paint — a Core Web Vital that measures the worst observed responsiveness to a user interaction during the page's lifecycle. Target <200ms. Replaced FID in March 2024." },
  { term: "JSON-LD", slug: "json-ld", definition: "The recommended syntax for structured data on the web — a `<script type=\"application/ld+json\">` block containing Schema.org markup. Easier to maintain than microdata or RDFa and the format LLMs parse most reliably." },
  { term: "Knowledge Graph", slug: "knowledge-graph", definition: "Google's database of entities and their relationships, drawn from Wikipedia, Wikidata, structured data and trusted sources. Inclusion correlates strongly with both classic SERP visibility and LLM citations." },
  { term: "LCP", slug: "lcp", definition: "Largest Contentful Paint — a Core Web Vital measuring when the largest above-the-fold element finishes rendering. Target <2.5s. Usually a hero image or H1." },
  { term: "llms.txt", slug: "llms-txt", definition: "A proposed convention (llmstxt.org) — a `/llms.txt` Markdown file at the site root that gives LLM crawlers a curated map of the most important URLs and a short site description. Pairs with llms-full.txt for full content snapshots.", readMore: "/blog/llms-txt-explained-2026", readMoreLabel: "llms.txt Explained" },
  { term: "LLMO", slug: "llmo", definition: "Large Language Model Optimization — an umbrella term overlapping AEO and GEO. Some practitioners use it specifically for ChatGPT/Claude/Gemini visibility distinct from Google AI Overviews; others use it interchangeably with AEO." },
  { term: "Local pack", slug: "local-pack", definition: "The 3-listing map module Google shows for local-intent queries ('plumber near me', 'best dentist Dhaka'). Ranking factors: proximity, GBP completeness, review velocity and citation consistency." },
  { term: "Meta description", slug: "meta-description", definition: "`<meta name=\"description\">` tag — Google may use it as the SERP snippet. Not a ranking factor, but a strong CTR lever. Aim for under 160 characters and front-load the value proposition." },
  { term: "noindex", slug: "noindex", definition: "A `<meta name=\"robots\" content=\"noindex\">` (or HTTP header) directive telling crawlers to drop the page from their index. Use for thin pages, internal tools and parametric URLs — never on your money pages by accident." },
  { term: "Organization schema", slug: "organization-schema", definition: "Schema.org Organization markup describing your brand entity with name, logo, sameAs links and contactPoint. The foundation of brand entity recognition for both Google and LLMs." },
  { term: "Person schema", slug: "person-schema", definition: "Schema.org Person markup describing an author or expert with name, jobTitle, sameAs profiles, alumniOf and hasCredential. Drives E-E-A-T and gets authors recognised as entities." },
  { term: "Product schema", slug: "product-schema", definition: "Schema.org Product markup with offers, price, availability, brand, gtin/mpn/sku and reviews. Required for Google Shopping rich results and increasingly used by AI shopping assistants." },
  { term: "sameAs", slug: "sameas", definition: "A schema.org property that links an entity to its canonical profiles elsewhere (LinkedIn, Wikidata, Crunchbase, GitHub). The strongest single signal for entity disambiguation." },
  { term: "Schema markup", slug: "schema-markup", definition: "Structured data (usually JSON-LD per schema.org) that describes page content in a machine-readable way. The top on-page lever for both rich results and AEO citations.", readMore: "/blog/schema-markup-for-aeo-2026", readMoreLabel: "Schema Markup for AEO" },
  { term: "SERP", slug: "serp", definition: "Search Engine Results Page — the actual page Google returns for a query. Modern SERPs include AI Overviews, People Also Ask, video carousels, image packs, local packs and 10 blue links; the mix varies by query intent." },
  { term: "Sitemap", slug: "sitemap", definition: "An XML file (commonly `/sitemap.xml`) listing the URLs you want search engines to crawl, with lastmod and priority hints. Critical for new and large sites where internal linking alone won't surface every URL." },
  { term: "Topical authority", slug: "topical-authority", definition: "The perceived depth of a site's expertise on a topic, judged by the breadth and quality of related content. Built through hub-and-spoke architectures, original research and consistent entity signals.", readMore: "/blog/internal-linking-at-scale-hub-and-spoke", readMoreLabel: "Hub-and-Spoke Internal Linking" },
  { term: "Vector embedding", slug: "vector-embedding", definition: "A numeric representation of text in high-dimensional space used by LLMs and modern retrieval systems for semantic search. Pages that are semantically close to a query — not just keyword-matched — get retrieved.", readMore: "/blog/vector-embeddings-for-seos-semantic-match-aeo", readMoreLabel: "Vector Embeddings for SEOs" },
  { term: "Wikidata", slug: "wikidata", definition: "Wikipedia's structured-data sibling — a free, machine-readable knowledge base. A Wikidata Q-id linked via Organization/Person `sameAs` is one of the strongest entity signals you can ship." },
  { term: "YMYL", slug: "ymyl", definition: "Your Money or Your Life — Google's term for topics that can materially affect health, finances or safety (medicine, law, finance, insurance). Held to a higher E-E-A-T bar; thin or anonymous content struggles to rank.", readMore: "/blog/seo-healthcare-clinics-ymyl-bd", readMoreLabel: "SEO for Healthcare (YMYL)" },
  { term: "Zero-click search", slug: "zero-click", definition: "A search where the user finds their answer on the SERP itself (AI Overview, featured snippet, knowledge panel) and never clicks through. Now the majority of informational queries — the central reason AEO matters." },
];

const FAQ_TERMS = ["aeo", "geo", "llmo", "eeat", "ymyl", "json-ld"];

function group(terms: Term[]) {
  const map = new Map<string, Term[]>();
  for (const t of terms) {
    const letter = t.term[0].toUpperCase();
    const k = /[A-Z]/.test(letter) ? letter : "#";
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(t);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}

export const Route = createFileRoute("/glossary")({
  head: () =>
    buildSeo({
      title: "SEO & AEO Glossary — 37 Terms Defined for 2026",
      description:
        "Plain-English definitions of every SEO, AEO and GEO term you need to know in 2026 — from Article schema to zero-click search. Citation-friendly and AI-ingestible.",
      path: "/glossary",
      ogEyebrow: "Glossary",
      image: "/og/glossary.jpg",
      keywords:
        "SEO glossary, AEO glossary, GEO glossary, SEO terms, AEO terms, schema glossary",
    }),
  component: GlossaryPage,
});

function GlossaryPage() {
  const sorted = [...TERMS].sort((a, b) => a.term.localeCompare(b.term));
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return sorted;
    return sorted.filter(
      (t) =>
        t.term.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        t.definition.toLowerCase().includes(q),
    );
  }, [q, sorted]);

  const grouped = group(filtered);

  const highlight = (text: string) => {
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q);
    if (i === -1) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark className="bg-primary/20 text-foreground rounded px-0.5">
          {text.slice(i, i + q.length)}
        </mark>
        {text.slice(i + q.length)}
      </>
    );
  };

  const definedTermSet = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": `${SITE.url}/glossary#set`,
    name: "SEO & AEO Glossary",
    url: `${SITE.url}/glossary`,
    hasDefinedTerm: sorted.map((t) => ({
      "@type": "DefinedTerm",
      "@id": `${SITE.url}/glossary#${t.slug}`,
      name: t.term,
      description: t.definition,
      inDefinedTermSet: `${SITE.url}/glossary#set`,
      url: `${SITE.url}/glossary#${t.slug}`,
    })),
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: sorted
      .filter((t) => FAQ_TERMS.includes(t.slug))
      .map((t) => ({
        "@type": "Question",
        name: `What is ${t.term}?`,
        acceptedAnswer: { "@type": "Answer", text: t.definition },
      })),
  };

  return (
    <>
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Glossary", path: "/glossary" },
        ])}
      />
      <JsonLd data={definedTermSet} />
      <JsonLd data={faqLd} />

      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Glossary", path: "/glossary" }]} />

      <header className="container-x pt-12 pb-8 lg:pt-20 lg:pb-12">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary mb-6">
          Glossary
        </div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
          The <span className="text-gradient">SEO &amp; AEO</span> glossary.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-3xl">
          {sorted.length} plain-English definitions of the terms that actually matter in 2026 —
          covering classic SEO, Answer Engine Optimization, Generative Engine Optimization,
          schema markup, Core Web Vitals, and local search. Every entry is short enough to be
          quoted by an LLM and links out to a deeper guide where one exists.
        </p>
      </header>

      <nav
        aria-label="Jump to letter"
        className="container-x sticky top-16 z-20 mb-10 border-y border-border bg-background/80 py-3 backdrop-blur"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label htmlFor="glossary-search" className="sr-only">
            Search glossary
          </label>
          <Input
            id="glossary-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${sorted.length} terms — e.g. “schema”, “LCP”, “entity”…`}
            className="h-11 max-w-md"
            autoComplete="off"
          />
          <ul className="flex flex-wrap gap-2 text-sm" role="list">
            {grouped.map(([letter]) => (
              <li key={letter}>
                <a
                  href={`#letter-${letter}`}
                  aria-label={`Jump to letter ${letter}`}
                  className="inline-flex h-11 w-11 sm:h-9 sm:w-9 items-center justify-center rounded-full border border-border hover:border-primary hover:text-primary transition"
                >
                  {letter}
                </a>
              </li>
            ))}
          </ul>
        </div>
        {q && (
          <p className="mt-3 text-sm text-muted-foreground" aria-live="polite">
            {filtered.length === 0
              ? `No terms match “${query}”.`
              : `${filtered.length} of ${sorted.length} terms match “${query}”.`}
          </p>
        )}
      </nav>

      <div className="container-x pb-32 space-y-14">
        {filtered.length === 0 && (
          <div className="rounded-lg border border-border p-8 text-center">
            <p className="text-muted-foreground">
              Nothing here yet. Try a broader term, or{" "}
              <button
                type="button"
                onClick={() => setQuery("")}
                className="text-primary underline-offset-4 hover:underline"
              >
                clear the search
              </button>
              .
            </p>
          </div>
        )}
        {grouped.map(([letter, items]) => (
          <section key={letter} id={`letter-${letter}`} className="scroll-mt-32">
            <h2 className="font-display text-4xl text-primary mb-6">{letter}</h2>
            <dl className="space-y-8">
              {items.map((t) => (
                <div key={t.slug} id={t.slug} className="scroll-mt-32 border-l-2 border-border pl-5">
                  <dt className="font-display text-xl md:text-2xl">
                    <h3 className="font-display text-xl md:text-2xl inline">
                      {highlight(t.term)}
                    </h3>
                  </dt>
                  <dd className="mt-2 text-muted-foreground leading-relaxed">
                    {highlight(t.definition)}
                    {t.readMore && (
                      <>
                        {" "}
                        <Link
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          to={t.readMore as any}
                          className="text-primary underline-offset-4 hover:underline"
                        >
                          {t.readMoreLabel ?? "Read more"} →
                        </Link>
                      </>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </>
  );
}